import { Controller } from "@hotwired/stimulus"

const COOLDOWN_DAYS = 14
const IOS_DELAY_MS = 10000
const STANDALONE_MEDIA = "(display-mode: standalone)"

export default class extends Controller {
  static targets = ["banner", "androidContent", "iosContent"]

  connect() {
    this.savedPrompt = null
    this.isInstalled = false

    if (this.isStandaloneOrInstalled()) return

    this.isIos = this.detectIos()
    this.setupAndroidListener()
    this.setupAppInstalledListener()

    if (this.isIos) {
      this.setupIosAutoShow()
    }
  }

  isStandaloneOrInstalled() {
    if (window.matchMedia(STANDALONE_MEDIA).matches) return true
    if (localStorage.getItem("pwa:installed") === "true") return true
    return false
  }

  shouldAutoShowSkip() {
    if (this.isStandaloneOrInstalled()) return true
    if (document.body.dataset.call === "active") return true

    const dismissedAt = localStorage.getItem("pwa:dismissed_at")
    if (dismissedAt) {
      const cooldownUntil = parseInt(dismissedAt, 10) + COOLDOWN_DAYS * 24 * 60 * 60 * 1000
      if (Date.now() < cooldownUntil) return true
    }

    const path = window.location.pathname
    if (path === "/login" || path.startsWith("/auth/")) return true
    if (path.endsWith("/chat")) return true

    return false
  }

  detectIos() {
    const ua = navigator.userAgent
    const isIosDevice = /iPad|iPhone|iPod/.test(ua)
    const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua)
    return isIosDevice && isSafari
  }

  setupAndroidListener() {
    const handler = (event) => {
      event.preventDefault()
      this.savedPrompt = event
      if (!this.shouldAutoShowSkip()) {
        this.showBanner()
      }
    }

    this.beforeInstallHandler = handler
    window.addEventListener("beforeinstallprompt", handler)
  }

  setupAppInstalledListener() {
    const handler = () => {
      localStorage.setItem("pwa:installed", "true")
      this.isInstalled = true
      this.hideBanner()
    }

    this.appInstalledHandler = handler
    window.addEventListener("appinstalled", handler)
  }

  setupIosAutoShow() {
    this.iosTimer = setTimeout(() => {
      if (this.shouldAutoShowSkip()) return
      this.showBanner()
    }, IOS_DELAY_MS)
  }

  showBanner() {
    if (!this.hasBannerTarget) return
    if (this.isInstalled) return
    if (document.body.dataset.call === "active") return

    if (this.savedPrompt) {
      this.androidContentTarget?.classList.remove("hidden")
      this.iosContentTarget?.classList.add("hidden")
    } else {
      this.androidContentTarget?.classList.add("hidden")
      this.iosContentTarget?.classList.remove("hidden")
    }

    this.bannerTarget.classList.remove("hidden")
  }

  hideBanner() {
    if (this.hasBannerTarget) {
      this.bannerTarget.classList.add("hidden")
    }
  }

  async install(event) {
    event.preventDefault()

    if (!this.savedPrompt) return

    this.savedPrompt.prompt()

    const { outcome } = await this.savedPrompt.userChoice
    this.savedPrompt = null

    if (outcome === "accepted") {
      localStorage.setItem("pwa:installed", "true")
      this.isInstalled = true
    } else {
      localStorage.setItem("pwa:dismissed_at", Date.now().toString())
    }

    this.hideBanner()
  }

  dismiss(event) {
    event.preventDefault()
    localStorage.setItem("pwa:dismissed_at", Date.now().toString())
    this.hideBanner()
  }

  openBanner(event) {
    event.preventDefault()

    if (this.isStandaloneOrInstalled()) return
    if (document.body.dataset.call === "active") return

    this.showBanner()
  }

  disconnect() {
    if (this.beforeInstallHandler) {
      window.removeEventListener("beforeinstallprompt", this.beforeInstallHandler)
    }
    if (this.appInstalledHandler) {
      window.removeEventListener("appinstalled", this.appInstalledHandler)
    }
    if (this.iosTimer) {
      clearTimeout(this.iosTimer)
    }
  }
}
